import React, { useState, useReducer, useEffect, CSSProperties } from "react";
// import { TextField } from "@material-ui/core";
// import MenuItem from "@material-ui/core/MenuItem";
// import { MuiPickersUtilsProvider, KeyboardDatePicker, DatePicker } from "@material-ui/pickers";
// import DateFnsUtils from "@date-io/date-fns";
import clsx from "clsx";
import debounce from "lodash.debounce";
import { UIButtonWithLoadingProgress } from "../components/ui/UIComponents";
import {
	DynamicFormValidation,
	DynamicFormField,
	getReducer,
	getInitialState,
} from "../laco-common/base/form-validation/dynamic";
import { FormLabel, PasswordInput, TextSelectInput, DateInput } from "./InputComponents/index";
import { NumberFormatPercentage, NumberFormatComma, NumberFormatInt, NumberFormatPhone } from "./formatters";
import GoogleAutoComplete from "../components/google-maps/AutoComplete";
import { UnmatchedPlace, Place } from "../models/googleMaps";
import { Address } from "../laco-common/base/models/address";
import ChipsInput from "./InputComponents/ChipsInput";
import ImageEdit from "./media/ImageEdit";
import { UICMSContent } from "../components/ui/UICMSContent";
import LinkifyText from "../laco-common/components/LinkifyText";
import EditorInput from "./InputComponents/EditorInput";
import TextBlockInput from "./listField/TextBlock";
import MediaInput from "./listField/Media";
import FormFieldInput from "./listField/FormField";
import FormFieldSectionInput from "./listField/FormSection";
import { IFormField } from "../laco-common";
import QuoteInput from "./listField/Quote";
import { getAddress } from "../laco-common/helpers/googleMaps";
import { QuotesCollection } from "../models/quote";
import CSSEditorInput from "./InputComponents/CSSEditorInput";

export type BaseFormProps = {
	title?: string | React.ReactNode;
	onCancel?: () => any;
};

export type DynamicFormProps = BaseFormProps & {
	form: DynamicFormValidation;
	submitBtn?: string | React.ReactNode;
	containerStyle?: CSSProperties;
	submitBtnClassName?: string;
	submitBtnTitle?: string;
	invalidSubmitBtnTitle?: string;
	actionName?: string;
	onSubmit?: () => Promise<any>;
	onFormUpdated?: (f: DynamicFormValidation) => any;
	resetForm?: () => any;
	title?: string | React.ReactNode;
	header?: string | React.ReactNode;
	description?: string | React.ReactNode;
	autoFocusFieldKey?: string;
	skipAutoFocus?: boolean;
	autoCompleteOff?: boolean;
	noFooter?: boolean;
	onChange?: (field: string, form: DynamicFormValidation) => any;
	// getForm?: () => DynamicFormValidation;
};

export type DynamicFormFieldProps = {
	field: DynamicFormField;
	getForm?: () => DynamicFormValidation;
	setForm?: (form: DynamicFormValidation) => any;
	updateListForms?: (key: string, forms: DynamicFormValidation[]) => DynamicFormValidation;
	index?: number;
	onChange?;
	onFieldChange?: (key: string, value: any) => any;
	onBlur?;
	processing?: boolean;
	getHelperText?: (field: DynamicFormField) => string;
	className?: string;
	omitLabel?: boolean;
	autoFocusFieldKey?: string;
	skipAutoFocus?: boolean;
};

export const DynamicFormFieldInput: React.FC<DynamicFormFieldProps> = (props: DynamicFormFieldProps) => {
	const field: DynamicFormField = props.field;
	console.debug("DynamicFormFieldInput field:", field, field.value);
	switch (field.fieldType) {
		case "date":
		case "datetime":
			return <DateInput {...props} />;
		// case "code":
		// 	return <CodeInput {...props} index={props.index} />;
		case "password":
			return <PasswordInput {...props} />;
		case "chips":
			return <ChipsInput {...props} index={props.index} />;
		case "address": {
			return (
				<GoogleAutoComplete
					field={field}
					label={<FormLabel field={field} />}
					placeholder={field.placeholder}
					enforceValidPlace={true}
					defaultValue={{ formatted_address: field.value?.formatted_address || field.value || "" } as Address}
					showImage={true}
					onPlaceSelected={(place: Place | UnmatchedPlace) => {
						const address = getAddress(place as Place);
						return props.onChange(address);
						// return props.customProps.onInputChange(props.customProps.field, place);
					}}
					addressImageProps={{ width: 320, height: 300 }}
				/>
			);
		}
		case "imageUrl":
		case "media": {
			return <ImageEdit {...props} />;
		}
		case "html": {
			return <EditorInput {...props} />;
		}
		case "css-editor": {
			return <CSSEditorInput {...props} />;
		}
		case "list":
			switch ((field as any as IFormField).listType) {
				// case "widget":
				// 	return <WidgetInput {...props} />;
				case "textBlock":
					return <TextBlockInput {...props} />;
				case "media":
					return <MediaInput {...props} />;
				case "formField":
					return <FormFieldInput {...props} />;
				case "formSection":
					return <FormFieldSectionInput {...props} />;
				case "quotes":
					const { quotes, quoters, profileImages, locations } = props.getForm().fields;
					const quotesProps: QuotesCollection = {
						quotes: quotes?.value,
						quoters: quoters?.value,
						profileImages: profileImages?.value,
						locations: locations?.value,
					};
					return (
						<QuoteInput
							{...props}
							{...quotesProps}
							onChange={(key: keyof QuotesCollection, value: string[]) => {
								console.log("Updating quotes key, value:", key, value);
								props.onFieldChange(key, value);
							}}
						/>
					);
			}

		case "phone":
		case "text":
		case "boolean":
		case "select":
		case "number":
		case "focusRow":
		default: {
			return <TextSelectInput {...props} />;
		}
	}
};

export const DynamicForm: React.FC<DynamicFormProps> = (props: DynamicFormProps) => {
	console.log("DynamicForm form:", props.form);
	const [form, setForm] = useState<DynamicFormValidation>(props.form);
	const [state, dispatch] = useReducer(getReducer(form), getInitialState(form));
	const [processing, setProcessing] = useState<boolean>(false);
	const [refresh, setRefresh] = useState<number>(0);

	const notifyFormChange = props.onChange
		? debounce((field: string, f: DynamicFormValidation) => props.onChange(field, f), 10, { trailing: true })
		: undefined;

	useEffect(() => {
		setData();
	}, [props.form, props.title, props.header]);

	useEffect(() => {
		if (props?.form?.refresh > 0) {
			setRefresh(refresh + 1);
		}
	}, [props?.form?.refresh]);

	const setData = () => {
		console.log("DynamicForm.setData(): props:", props);
		setForm(props.form);
		dispatch({
			type: "updateForm",
			updateForm: true,
			payload: props.form,
		});
	};

	let index: number = -1;

	return (
		<React.Fragment key={refresh}>
			{!!form && (
				<form noValidate autoComplete={props.autoCompleteOff ? "off" : "on"}>
					{props.title && <h2 className="mb-4_5">{props.title}</h2>}
					{props.description}
					<div className="dynamic-form-fields" style={props.containerStyle}>
						{form.getOrderedFields().map((field: DynamicFormField) => {
							index++;
							console.debug("DynamicForm field:", field);
							if ((field.renderWhen && !field.renderWhen(field, form)) || field.hide) return null;
							return (
								<div
									key={field.order}
									className={clsx(
										"form-row w-100 mb-4",
										field.renderWhen && "animated slideInDown fast-1"
									)}
								>
									<DynamicFormFieldInput
										field={field}
										getForm={() => form}
										setForm={(f) => setForm(f)}
										index={index}
										autoFocusFieldKey={props.autoFocusFieldKey}
										skipAutoFocus={props.skipAutoFocus}
										processing={processing}
										onChange={
											field.fieldType === "date" ||
											field.fieldType === "datetime" ||
											field.fieldType === "css-editor"
												? (value: Date) => {
														dispatch({
															type: field.key,
															payload: value,
														});
														if (notifyFormChange) notifyFormChange(field.key, form);
												  }
												: (event) => {
														dispatch({
															type: field.key,
															payload: event?.target ? event.target.value : event,
														});
														if (notifyFormChange) notifyFormChange(field.key, form);
												  }
										}
										onFieldChange={(key: string, value: any) => {
											dispatch({
												type: key,
												payload: value,
											});
											if (notifyFormChange) notifyFormChange(key, form);
										}}
										// updateListForms={
										// 	field.fieldType === "list"
										// 		? (key: string, forms: DynamicFormValidation[]) => {
										// 				const _form: DynamicFormValidation = clonedeep(form);
										// 				_form.fields[key].forms = forms;
										// 				if (props.onFormUpdated) props.onFormUpdated(_form);
										// 				setForm(_form);
										// 				return form;
										// 		  }
										// 		: undefined
										// }
										// onBlur={(event) => {
										// 	dispatch({
										// 		type: field.key,
										// 		payload: field.value,
										// 	});
										// }}
										getHelperText={(field: DynamicFormField) => form.getErrMsg(field.key, true)}
									/>
									{(field.displayContentLinkified ||
										field.key === "description" ||
										field.fieldType === "link") &&
										!!field.value && (
											<div className="p-2 linkify-preview w-100">
												{/* <UICMSContent content={field.value} /> */}
												<LinkifyText text={field.value} />
											</div>
										)}
								</div>
							);
						})}
					</div>
					{!props.noFooter && <FormFooter {...props} onProcessingUpdated={(value) => setProcessing(value)} />}
				</form>
			)}
		</React.Fragment>
	);
};

type FormFooterProps = DynamicFormProps & {
	form: DynamicFormValidation;
	onProcessingUpdated?: (value: boolean) => any;
	children?: React.ReactNode;
};

export const FormFooter: React.FC<FormFooterProps> = (props: FormFooterProps) => {
	const [processing, setProcessing] = useState<boolean>(false);
	const { form } = props;

	const onCancel = () => {
		console.log("DynamicForm.onCancel()");
		if (props.onCancel) props.onCancel();
	};

	return (
		<div className="form-row d-flex justify-content-end">
			{props.children}
			{props.resetForm && (
				<button className="btn btn-default mt-2 mr-3" type="reset" onClick={() => props.resetForm()}>
					Reset
				</button>
			)}
			{props.onCancel && (
				<button className="btn btn-link mt-2 mr-3" type="button" onClick={() => onCancel()}>
					Cancel
				</button>
			)}
			<UIButtonWithLoadingProgress
				type="submit"
				onClick={() => {
					setProcessing(true);
					if (props.onProcessingUpdated) props.onProcessingUpdated(true);
					return props
						.onSubmit()
						.then((result) => {
							setProcessing(false);
							if (props.onProcessingUpdated) props.onProcessingUpdated(false);
							return result;
						})
						.catch(() => {
							setProcessing(false);
							if (props.onProcessingUpdated) props.onProcessingUpdated(false);
							return null;
						});
				}}
				className={clsx("btn btn-primary mt-2", props.submitBtnClassName)}
				actionName={props.actionName || "Saving..."}
				btnTitle={
					form && !form.isValid ? props.invalidSubmitBtnTitle || "Fix form errors" : props.submitBtnTitle
				}
				disabled={processing || !form}
			>
				{props.submitBtn}
			</UIButtonWithLoadingProgress>
		</div>
	);
};
